/**
 * Per-date minimum-stay ranges for scrape-and-resell operators.
 *
 * Almaza sets `minimalStay` PER DATE (3 nights in shoulder season, 7 in peak —
 * 79 of 152 units vary across the season). We used to store ONE number per unit,
 * which was both wrong and unsafe: taking the peak protects the booking but
 * over-restricts genuine short off-peak stays; taking anything lower gets the
 * booking rejected by the operator after we've confirmed it (L-054).
 *
 * The almaza-ical sync publishes range-compressed minimums per unit in its
 * GitHub-Pages `index.json` (same place the availability feeds come from), so we
 * read them here and let the booking flow apply the minimum that actually
 * governs the guest's check-in date.
 *
 * Fail-soft by design: any fetch/parse problem returns [] and the caller falls
 * back to `units.min_nights` (the peak) — i.e. we degrade to "safe but strict",
 * never to "permissive".
 *
 * Lemon Spaces (2026-10-05) does the same thing on Hostaway: the storefront shows
 * one minimum per unit (what `units.min_nights` copies) while the calendar raises
 * it per date — 3 nights on most Zamalek dates, 7 on some El Gouna dates. We were
 * quoting 2-night stays the operator refuses on 46 units. `lemon-ical/sync.js`
 * now publishes the ranges in its feed index. NOTE the fallback there is the
 * storefront minimum, which is the LOWER number — for Lemon a failed read is
 * permissive, so the silent live check is the backstop, not this file.
 */

/** Feed index per source. Almaza: `{properties:[{wp,…}]}`; Lemon: `{[wp]:{…}}`. */
const INDEX_URLS: Record<string, string> = {
  almaza: "https://mohamedmaged3002-droid.github.io/almaza-ical/index.json",
  lemonspaces: "https://feeds.bluekeys.co/lemon/index.json",
};
const REVALIDATE_SECONDS = 60 * 60; // 1h, matches the iCal feed cache

export interface MinStayRange {
  /** inclusive yyyy-mm-dd */
  from: string;
  /** inclusive yyyy-mm-dd */
  to: string;
  /** minimum nights for a stay CHECKING IN within [from, to] */
  min: number;
}

interface IndexEntry {
  wp: number;
  minStayRanges?: MinStayRange[];
}

/** One unit's ranges out of either index shape; [] for anything malformed. */
export function rangesFromIndex(json: unknown, wpPostId: number): MinStayRange[] {
  if (!json || typeof json !== "object") return [];
  const props = (json as { properties?: IndexEntry[] }).properties;
  const entry = Array.isArray(props)
    ? props.find((p) => p?.wp === wpPostId)
    : (json as Record<string, IndexEntry | undefined>)[String(wpPostId)];
  const ranges = entry?.minStayRanges;
  if (!Array.isArray(ranges)) return [];
  // Guard against a malformed publish — a bad row must not become "min 0".
  return ranges.filter(
    (r) => r && typeof r.min === "number" && r.min > 0 && typeof r.from === "string" && typeof r.to === "string",
  );
}

/**
 * Range-compressed per-date minimums for one unit, or [] when unknown.
 * Only Almaza and Lemon Spaces publish these; every other source returns []
 * without a network call.
 */
export async function getMinStayRanges(
  wpPostId: number | null | undefined,
  source: string | null | undefined,
): Promise<MinStayRange[]> {
  const url = INDEX_URLS[(source ?? "").toLowerCase()];
  if (!wpPostId || !url) return [];
  try {
    const res = await fetch(url, { next: { revalidate: REVALIDATE_SECONDS } });
    if (!res.ok) return [];
    return rangesFromIndex(await res.json(), wpPostId);
  } catch {
    return [];
  }
}

/**
 * The minimum nights that applies to a given check-in date.
 * Falls back to `fallback` (the unit's stored peak) when no range covers it.
 */
export function minNightsForCheckin(
  checkinIso: string | null | undefined,
  ranges: MinStayRange[] | null | undefined,
  fallback: number | null | undefined,
): number {
  const fb = fallback ?? 0;
  if (!checkinIso || !ranges?.length) return fb;
  const hit = ranges.find((r) => checkinIso >= r.from && checkinIso <= r.to);
  return hit ? hit.min : fb;
}
